import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAccessToken } from './lib/api';
import { disconnectSocket } from './lib/socket';
import type { Me } from './types';

type AuthContextValue = {
  me: Me | null;
  loading: boolean;
  loginEmail: (email: string, password: string) => Promise<void>;
  loginPhone: (phone: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const m = await api<Me>('/api/v1/auth/me');
      setMe(m);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(
          ((import.meta.env.VITE_API_URL ?? '') as string).replace(/\/$/, '') + '/api/v1/auth/refresh',
          { method: 'POST', credentials: 'include' },
        );
        if (r.ok) {
          const data = (await r.json()) as { accessToken: string };
          setAccessToken(data.accessToken);
          await refreshMe();
        }
      } catch {
        /* not logged in */
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshMe]);

  const loginEmail = useCallback(async (email: string, password: string) => {
    const data = await api<{ accessToken: string; user: Me }>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
      noRefresh: true,
    });
    setAccessToken(data.accessToken);
    await refreshMe();
  }, [refreshMe]);

  const loginPhone = useCallback(async (phone: string) => {
    const data = await api<{ accessToken: string; user: Me }>('/api/v1/auth/login-phone', {
      method: 'POST',
      body: { phone },
      noRefresh: true,
    });
    setAccessToken(data.accessToken);
    await refreshMe();
  }, [refreshMe]);

  const logout = useCallback(async () => {
    try {
      await api('/api/v1/auth/logout', { method: 'POST', noRefresh: true });
    } catch { /* ignore */ }
    setAccessToken(null);
    disconnectSocket();
    setMe(null);
    const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> };
    nav.clearAppBadge?.().catch(() => {});
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ me, loading, loginEmail, loginPhone, logout, refreshMe }),
    [me, loading, loginEmail, loginPhone, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
